import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ImageIcon } from "lucide-react";
import { ImageUpload } from "@/components/ImageUpload";

const empty = {
    image_url: "",
    heading: "",
    subheading: "",
    brief: "",
    cta_label: "",
    cta_link: "",
    display_order: 0,
    is_active: true,
};

export default function HeroSlidesTab() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null); // null | {} (new) | row
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);

    const reload = () => api.get("/admin/hero-slides").then(({ data }) => setItems(data)).catch(() => setItems([]));
    useEffect(() => { reload(); }, []);

    const startNew = () => { setEditing({}); setForm(empty); };
    const startEdit = (row) => { setEditing(row); setForm({ ...empty, ...row }); };
    const cancelEdit = () => { setEditing(null); setForm(empty); };

    const onChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((f) => ({
            ...f,
            [name]: type === "checkbox" ? checked : (name === "display_order" ? Number(value) : value),
        }));
    };

    const save = async (e) => {
        e.preventDefault();
        if (!form.image_url) { toast.error("Please upload a slide image"); return; }
        if (!form.heading?.trim()) { toast.error("Heading is required"); return; }
        setSaving(true);
        try {
            const payload = { ...form };
            if (editing?.id) {
                await api.patch(`/admin/hero-slides/${editing.id}`, payload);
                toast.success("Slide updated");
            } else {
                await api.post("/admin/hero-slides", payload);
                toast.success("Slide created");
            }
            cancelEdit();
            reload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSaving(false);
        }
    };

    const remove = async (row) => {
        if (!window.confirm(`Delete slide "${row.heading}"?`)) return;
        try {
            await api.delete(`/admin/hero-slides/${row.id}`);
            toast.success("Slide deleted");
            reload();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
    };

    return (
        <div data-testid="hero-slides-tab">
            <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
                <div>
                    <h2 className="font-heading text-2xl font-semibold">Hero slider</h2>
                    <p className="text-sm text-ink-700 mt-0.5">Slides shown on the homepage hero (auto-advance every 5s).</p>
                </div>
                {!editing && (
                    <Button onClick={startNew} className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta" data-testid="hero-slide-new-btn">
                        <Plus className="h-4 w-4 mr-1" /> New slide
                    </Button>
                )}
            </div>

            {editing && (
                <form onSubmit={save} className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5 space-y-4 mb-6" data-testid="hero-slide-form">
                    <div>
                        <Label>Slide image</Label>
                        <div className="mt-1.5">
                            <ImageUpload
                                value={form.image_url}
                                onChange={(url) => setForm((f) => ({ ...f, image_url: url || "" }))}
                                testIdPrefix="hero-slide-image"
                            />
                        </div>
                        <p className="text-[11px] text-ink-400 mt-1">Landscape works best. Recommended ~1600×900.</p>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label>Heading</Label>
                            <Input name="heading" value={form.heading} onChange={onChange} required maxLength={200} className="mt-1.5 rounded-xl" data-testid="hero-slide-heading-input" />
                        </div>
                        <div>
                            <Label>Sub-heading (eyebrow)</Label>
                            <Input name="subheading" value={form.subheading || ""} onChange={onChange} maxLength={200} placeholder="e.g. Find your inner calm" className="mt-1.5 rounded-xl" data-testid="hero-slide-subheading-input" />
                        </div>
                    </div>
                    <div>
                        <Label>Brief</Label>
                        <Textarea name="brief" value={form.brief || ""} onChange={onChange} rows={3} maxLength={500} className="mt-1.5 rounded-xl" data-testid="hero-slide-brief-input" />
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                            <Label>CTA label</Label>
                            <Input name="cta_label" value={form.cta_label || ""} onChange={onChange} maxLength={60} placeholder="e.g. Explore Courses" className="mt-1.5 rounded-xl" data-testid="hero-slide-cta-label-input" />
                        </div>
                        <div>
                            <Label>CTA link</Label>
                            <Input name="cta_link" value={form.cta_link || ""} onChange={onChange} maxLength={200} placeholder="/membership or #courses or https://..." className="mt-1.5 rounded-xl" data-testid="hero-slide-cta-link-input" />
                        </div>
                        <div>
                            <Label>Display order</Label>
                            <Input type="number" name="display_order" value={form.display_order} onChange={onChange} className="mt-1.5 rounded-xl" />
                        </div>
                        <label className="flex items-end gap-2 text-sm pb-2.5">
                            <input type="checkbox" name="is_active" checked={form.is_active} onChange={onChange} />
                            Active (visible on homepage)
                        </label>
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={cancelEdit} className="rounded-full">Cancel</Button>
                        <Button type="submit" disabled={saving} className="rounded-full bg-brand-500 hover:bg-brand-600 text-white shadow-cta" data-testid="hero-slide-save-btn">
                            {saving ? "Saving…" : editing?.id ? "Save changes" : "Create slide"}
                        </Button>
                    </div>
                </form>
            )}

            <div className="space-y-3">
                {items.length === 0 && !editing && (
                    <div className="bg-white rounded-2xl border border-dashed border-gray-200 p-10 text-center text-ink-700">
                        No slides yet — click "New slide" to add one.
                    </div>
                )}
                {items.map((s) => (
                    <div key={s.id} className="rounded-2xl bg-white border border-gray-100 shadow-soft p-3 flex items-center gap-3" data-testid={`hero-slide-row-${s.id}`}>
                        <div className="h-20 w-32 rounded-xl overflow-hidden bg-gray-100 shrink-0">
                            {s.image_url ? (
                                <img src={s.image_url} alt={s.heading} className="w-full h-full object-cover" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-ink-400"><ImageIcon className="h-5 w-5" /></div>
                            )}
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                                <div className="font-heading font-semibold text-ink-900 truncate">{s.heading}</div>
                                {!s.is_active && <span className="text-[10px] uppercase bg-gray-100 text-ink-700 rounded-full px-2 py-0.5">hidden</span>}
                            </div>
                            {s.subheading && <div className="text-xs text-brand-600 uppercase tracking-wider mt-0.5">{s.subheading}</div>}
                            <div className="text-xs text-ink-700 mt-1 truncate">{s.brief}</div>
                            <div className="text-[11px] text-ink-400 mt-1">Order {s.display_order} {s.cta_label ? `· CTA → ${s.cta_link}` : ""}</div>
                        </div>
                        <div className="flex gap-1.5 shrink-0">
                            <Button size="icon" variant="outline" className="rounded-full h-9 w-9" onClick={() => startEdit(s)} data-testid={`hero-slide-edit-${s.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                            <Button size="icon" variant="outline" className="rounded-full h-9 w-9 text-accent-700 hover:bg-accent-50" onClick={() => remove(s)} data-testid={`hero-slide-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
